(() => {
  if (customElements.get('ff-header-search')) return;

  class FFHeaderSearch extends HTMLElement {
    connectedCallback() {
      if (this.listeners) return;
      this.form = this.querySelector('form');
      this.input = this.querySelector('input[type="search"]');
      this.panel = this.querySelector('[data-search-panel]');
      this.list = this.querySelector('[role="listbox"]');
      this.message = this.querySelector('[data-search-message]');
      this.status = this.querySelector('[data-search-status]');
      this.resetButton = this.querySelector('button[type="reset"]');
      this.allResults = this.querySelector('[data-all-results]');
      if (!this.form || !this.input || !this.panel || !this.list) return;

      this.listeners = new AbortController();
      this.requestId = (this.requestId || 0) + 1;
      this.cache = new Map();
      this.options = [];
      this.selectedIndex = -1;
      this.composing = false;
      this.predictiveEnabled = this.dataset.predictiveEnabled === 'true';
      const listenerOptions = { signal: this.listeners.signal };

      this.input.addEventListener('input', () => this.queueSearch(), listenerOptions);
      this.input.addEventListener('compositionstart', () => {
        this.composing = true;
        this.close(true);
      }, listenerOptions);
      this.input.addEventListener('compositionend', () => {
        this.composing = false;
        this.queueSearch();
      }, listenerOptions);
      this.form.addEventListener('reset', (event) => {
        event.preventDefault();
        this.input.value = '';
        this.close(true);
        this.updateControls();
        this.input.focus();
      }, listenerOptions);
      this.addEventListener('focusin', (event) => {
        window.clearTimeout(this.focusOutTimer);
        if (this.suppressFocusSearch) {
          this.suppressFocusSearch = false;
          return;
        }
        if (event.target === this.input && !this.hasAttribute('open') && this.query()) {
          this.queueSearch(true);
        }
      }, listenerOptions);
      this.addEventListener('focusout', () => {
        this.focusOutTimer = window.setTimeout(() => {
          if (!this.contains(document.activeElement)) this.close();
        }, 0);
      }, listenerOptions);
      this.addEventListener('keydown', (event) => this.onKeydown(event), listenerOptions);
      this.addEventListener('keyup', (event) => {
        if (event.key === 'Escape' && this.escapeHandled) {
          event.stopPropagation();
          this.escapeHandled = false;
        }
      }, listenerOptions);
      document.addEventListener('pointerdown', (event) => {
        if (!this.contains(event.target)) this.close();
      }, listenerOptions);
      const viewportOptions = { ...listenerOptions, passive: true };
      const schedulePanelHeight = () => this.schedulePanelHeight();
      window.addEventListener('resize', schedulePanelHeight, viewportOptions);
      window.addEventListener('scroll', schedulePanelHeight, viewportOptions);
      window.visualViewport?.addEventListener('resize', schedulePanelHeight, viewportOptions);
      window.visualViewport?.addEventListener('scroll', schedulePanelHeight, viewportOptions);

      try {
        this.money = new Intl.NumberFormat(this.dataset.locale || 'de-DE', {
          style: 'currency', currency: this.dataset.currency || 'EUR'
        });
      } catch (_) {
        this.money = new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' });
      }
      this.close(true);
      this.updateControls();
    }

    disconnectedCallback() {
      this.invalidateRequest();
      this.clearPanelHeight();
      window.clearTimeout(this.focusOutTimer);
      this.listeners?.abort();
      this.listeners = null;
    }

    query() {
      return this.input.value.trim();
    }

    displayProductTitle(value) {
      // Keep wash-age word boundaries aligned with ff-display-product-title.liquid.
      return String(value ?? '').trim().replace(
        /(^|[^A-Za-z0-9_äöüÄÖÜß])([0-9]+)M(?=$|[^A-Za-z0-9_äöüÄÖÜß])/g,
        (_, boundary, months) => `${boundary}${months} ${Number(months) === 1 ? 'Monat' : 'Monate'}`
      );
    }

    updateControls() {
      if (this.resetButton) this.resetButton.hidden = this.input.value.length === 0;
      if (this.allResults) {
        const url = new URL(this.form.action, window.location.href);
        url.searchParams.set('q', this.query());
        url.searchParams.set('options[prefix]', 'last');
        this.allResults.href = url.href;
      }
    }

    invalidateRequest() {
      this.requestId = (this.requestId || 0) + 1;
      window.clearTimeout(this.debounceTimer);
      this.requestController?.abort();
      this.requestController = null;
      this.removeAttribute('loading');
      this.list?.removeAttribute('aria-busy');
    }

    close(clearResults = false) {
      this.invalidateRequest();
      this.clearPanelHeight();
      this.panel.hidden = true;
      this.removeAttribute('open');
      this.input.setAttribute('aria-expanded', 'false');
      this.select(-1);
      if (this.status) this.status.textContent = '';
      if (clearResults) {
        this.list.replaceChildren();
        this.options = [];
        if (this.message) this.message.hidden = true;
      }
    }

    queueSearch(immediate = false) {
      this.close(true);
      this.updateControls();
      const term = this.query();
      if (this.composing || !term) return;
      if (this.isReturnQuery()) this.render([]);
      if (!this.predictiveEnabled) return;
      const requestId = this.requestId;
      if (immediate) this.search(term, requestId);
      else this.debounceTimer = window.setTimeout(() => this.search(term, requestId), 300);
    }

    isCurrent(term, requestId) {
      return this.isConnected && this.listeners && !this.composing &&
        requestId === this.requestId && term === this.query() &&
        this.contains(document.activeElement);
    }

    isReturnQuery() {
      const terms = new Set(['retoure', 'retouren', 'retour', 'retoute', 'rucksendung', 'rucksendungen', 'ruecksendung', 'ruecksendungen', 'ruckgabe', 'ruckgaben', 'rueckgabe', 'rueckgaben', 'return', 'returns']);
      return this.query().toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '')
        .split(/[^a-z0-9]+/).some(word => terms.has(word));
    }

    addReturnOption() {
      if (!this.isReturnQuery()) return;
      const item = document.createElement('li');
      item.className = 'ff-header-search__item ff-header-search__item--service';
      item.id = `${this.list.id}-return`;
      item.setAttribute('role', 'option');
      item.setAttribute('aria-selected', 'false');
      const link = document.createElement('a');
      link.className = 'ff-header-search__link ff-header-search__link--service';
      link.href = this.dataset.returnUrl;
      link.tabIndex = -1;
      const details = document.createElement('span');
      details.className = 'ff-header-search__details';
      const title = document.createElement('span');
      title.className = 'ff-header-search__title';
      title.textContent = this.dataset.returnTitle;
      const description = document.createElement('span');
      description.className = 'ff-header-search__service-description';
      description.textContent = this.dataset.returnDescription;
      details.append(title, description);
      link.append(details);
      item.append(link);
      this.list.append(item);
      this.options.push(item);
    }

    async search(term, requestId) {
      if (!this.isCurrent(term, requestId)) return;
      if (this.cache.has(term)) {
        this.render(this.cache.get(term));
        return;
      }
      this.requestController = new AbortController();
      this.setAttribute('loading', '');
      this.list.setAttribute('aria-busy', 'true');
      this.showMessage(this.dataset.loadingText || '');

      try {
        const url = new URL(this.dataset.url, window.location.origin);
        if (url.origin !== window.location.origin) throw new Error('Invalid search URL');
        if (!url.pathname.endsWith('.json')) url.pathname += '.json';
        url.searchParams.set('q', term);
        url.searchParams.set('resources[type]', 'product');
        url.searchParams.set('resources[limit]', '6');
        url.searchParams.set('resources[options][unavailable_products]', 'last');
        url.searchParams.set('resources[options][fields]', 'title,product_type,variants.title,vendor,variants.sku,tag');
        const response = await fetch(url.href, {
          signal: this.requestController.signal,
          credentials: 'same-origin',
          headers: { Accept: 'application/json' }
        });
        if (!response.ok) throw new Error('Search unavailable');
        const data = await response.json();
        if (!this.isCurrent(term, requestId)) return;
        const products = data?.resources?.results?.products;
        if (!Array.isArray(products)) throw new Error('Invalid search response');
        this.cache.set(term, products);
        if (this.cache.size > 20) this.cache.delete(this.cache.keys().next().value);
        this.render(products);
      } catch (error) {
        if (error.name === 'AbortError' || !this.isCurrent(term, requestId)) return;
        this.showMessage(this.dataset.errorText || '');
      } finally {
        if (this.isCurrent(term, requestId)) {
          this.removeAttribute('loading');
          this.list.removeAttribute('aria-busy');
          this.requestController = null;
        }
      }
    }

    openPanel() {
      this.panel.hidden = false;
      this.setAttribute('open', '');
      this.input.setAttribute('aria-expanded', 'true');
      this.schedulePanelHeight();
    }

    schedulePanelHeight() {
      if (!this.hasAttribute('open') || this.viewportFrame != null) return;
      this.viewportFrame = window.requestAnimationFrame(() => {
        this.viewportFrame = null;
        if (!this.isConnected || !this.hasAttribute('open')) return;
        const viewport = window.visualViewport;
        const visibleBottom = viewport ? viewport.height + viewport.offsetTop : window.innerHeight;
        const availableHeight = Math.max(0, Math.floor(visibleBottom - this.panel.getBoundingClientRect().top - 12));
        this.style.setProperty('--ff-search-available-height', `${availableHeight}px`);
      });
    }

    clearPanelHeight() {
      if (this.viewportFrame != null) window.cancelAnimationFrame(this.viewportFrame);
      this.viewportFrame = null;
      this.style.removeProperty('--ff-search-available-height');
    }

    showMessage(text) {
      if (this.message) {
        this.message.textContent = text;
        this.message.hidden = !text;
      }
      if (this.status) this.status.textContent = text;
      this.openPanel();
    }

    productURL(value) {
      if (typeof value !== 'string' || !value) return null;
      try {
        const url = new URL(value, window.location.origin);
        return url.origin === window.location.origin && /^https?:$/.test(url.protocol) ? url : null;
      } catch (_) {
        return null;
      }
    }

    amount(value) {
      if (value === null || value === undefined || value === '') return null;
      const number = Number(value);
      return Number.isFinite(number) && number >= 0 ? number : null;
    }

    render(products) {
      const selectedURL = this.options?.[this.selectedIndex]?.querySelector('a')?.href;
      this.list.replaceChildren();
      this.options = [];
      this.selectedIndex = -1;
      this.input.removeAttribute('aria-activedescendant');

      this.addReturnOption();

      products.slice(0, 6).forEach((product) => {
        const url = this.productURL(product.url);
        if (!url) return;
        const item = document.createElement('li');
        item.className = 'ff-header-search__item';
        item.id = `${this.list.id}-option-${this.options.length + 1}`;
        item.setAttribute('role', 'option');
        item.setAttribute('aria-selected', 'false');
        const link = document.createElement('a');
        link.className = 'ff-header-search__link';
        link.href = url.href;
        link.tabIndex = -1;

        const imageSource = typeof product.image === 'string' ? product.image :
          (product.image?.url || product.image?.src || product.featured_image?.url || product.featured_image?.src);
        if (imageSource) {
          try {
            const imageURL = new URL(imageSource, window.location.origin);
            if (imageURL.protocol === 'https:' || imageURL.origin === window.location.origin) {
              const image = document.createElement('img');
              image.className = 'ff-header-search__image';
              image.src = imageURL.href;
              image.alt = '';
              image.width = 64;
              image.height = 80;
              image.loading = 'lazy';
              link.append(image);
            }
          } catch (_) { /* A missing image must not disable a product link. */ }
        }

        const details = document.createElement('span');
        details.className = 'ff-header-search__details';
        const title = document.createElement('span');
        title.className = 'ff-header-search__title';
        title.textContent = this.displayProductTitle(product.title);
        details.append(title);

        // Ajax predictive search returns decimal currency units, unlike Liquid money filters.
        const variant = Array.isArray(product.variants) && product.variants.length === 1 ? product.variants[0] : null;
        const variantPrice = this.amount(variant?.price);
        const price = variantPrice ?? this.amount(product.price_min) ?? this.amount(product.price);
        const maximum = this.amount(product.price_max);
        const compare = this.amount(variant?.compare_at_price) ?? this.amount(product.compare_at_price_min) ?? this.amount(product.compare_at_price);
        if (price !== null) {
          const prices = document.createElement('span');
          prices.className = 'ff-header-search__prices';
          const priceLabel = document.createElement('span');
          priceLabel.className = 'ff-header-search__price';
          const from = variantPrice === null && maximum !== null && maximum > price && this.dataset.fromText;
          priceLabel.textContent = `${from ? `${from} ` : ''}${this.money.format(price)}`;
          prices.append(priceLabel);
          if (compare !== null && compare > price) {
            const compareLabel = document.createElement('s');
            compareLabel.className = 'ff-header-search__compare';
            compareLabel.textContent = this.money.format(compare);
            prices.append(compareLabel);
          }
          details.append(prices);
        }

        link.append(details);
        item.append(link);
        this.list.append(item);
        this.options.push(item);
      });

      if (selectedURL) {
        this.select(this.options.findIndex(option => option.querySelector('a').href === selectedURL));
      }

      if (!this.options.length) {
        this.showMessage(this.dataset.emptyText || '');
      } else {
        if (this.message) this.message.hidden = true;
        if (this.status) {
          this.status.textContent = (this.isReturnQuery() ? this.dataset.returnResults : (this.dataset.resultsText || '{{ count }}'))
            .replace(/__COUNT__|\{\{\s*count\s*\}\}/g, String(this.options.length));
        }
        this.openPanel();
      }
    }

    select(index) {
      this.options?.forEach((option, optionIndex) => {
        const selected = optionIndex === index;
        option.setAttribute('aria-selected', String(selected));
        option.querySelector('a').classList.toggle('is-selected', selected);
      });
      this.selectedIndex = index;
      const selected = this.options?.[index];
      if (selected) {
        this.input.setAttribute('aria-activedescendant', selected.id);
        selected.scrollIntoView?.({ block: 'nearest' });
      } else {
        this.input.removeAttribute('aria-activedescendant');
      }
    }

    onKeydown(event) {
      if (this.composing || event.isComposing || event.keyCode === 229) return;
      // Keep the theme's global Escape handler from hiding the focused sticky header.
      if (event.key === 'Escape') {
        event.preventDefault();
        event.stopPropagation();
        this.escapeHandled = true;
        this.close();
        if (document.activeElement !== this.input) {
          this.suppressFocusSearch = true;
          this.input.focus();
        }
        return;
      }
      if (event.target !== this.input || !this.hasAttribute('open')) return;
      if ((event.key === 'ArrowDown' || event.key === 'ArrowUp') && this.options.length) {
        event.preventDefault();
        const next = event.key === 'ArrowDown' ?
          (this.selectedIndex + 1) % this.options.length :
          (this.selectedIndex < 0 ? this.options.length - 1 : (this.selectedIndex - 1 + this.options.length) % this.options.length);
        this.select(next);
      } else if (event.key === 'Enter' && this.selectedIndex >= 0) {
        event.preventDefault();
        this.options[this.selectedIndex].querySelector('a').click();
      }
    }
  }

  customElements.define('ff-header-search', FFHeaderSearch);
})();
